/**
 * ResolveDocumentBlockerPanel — P0 Governed Document Blocker Resolution.
 *
 * Right-side slide-over panel.  Staff resolves per-item governed document
 * requirements without leaving the Cockpit context.
 *
 * Architecture rules:
 *  - Backend is the sole authority on suppression state.  Panel renders backend data.
 *  - After every successful mutation: refetch items + trigger runDrl() + onRefreshCockpit().
 *  - request_from_client is NOT terminal — blocker stays active; responsibility → CLIENT.
 *  - Suppression only when ALL governing+production items have terminal dispositions.
 *
 * Props:
 *  open            — boolean: panel visible
 *  onClose         — () => void
 *  clientId        — string
 *  engagementId    — string
 *  drlData         — latest DRL assessment (for needed_to_resolve refresh)
 *  runDrl          — () => void: refresh DRL presentation after mutation
 *  onRefreshCockpit — () => void: refresh Cockpit after mutation
 *  apiBase         — REACT_APP_BACKEND_URL
 *  token           — JWT token
 */
import { useState, useEffect, useCallback, useRef } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";

/**
 * safeJson — parse a fetch Response as JSON without ever throwing a
 * SyntaxError to the caller.  If the body is not valid JSON (e.g. an
 * HTML 500 page from the proxy) we return a synthetic error envelope
 * so upstream error-display logic always gets a consistent object.
 */
async function safeJson(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return { detail: `Server error (HTTP ${res.status})` };
  }
}

// ─── constants ────────────────────────────────────────────────────────────────

const TERMINAL_DISPOSITIONS = new Set([
  "received",
  "satisfied_by_existing",
  "not_applicable",
  "proceed_without",
  "duplicate",
]);

const NOTES_REQUIRED_FOR = new Set(["not_applicable", "proceed_without", "duplicate"]);

const DISPOSITION_LABELS = {
  received:              "Document Received",
  satisfied_by_existing: "Satisfied by Existing Document",
  not_applicable:        "Not Required / Not Applicable",
  proceed_without:       "Proceed Without — Staff Judgment",
  duplicate:             "Duplicate / Superseded",
  request_sent_to_client: "Requested from Client",
  keep_open:             "Keep Open",
};

const DISPOSITION_ICON = {
  received:              "CheckCircle2",
  satisfied_by_existing: "FolderCheck",
  not_applicable:        "XCircle",
  proceed_without:       "UserCheck",
  duplicate:             "Copy",
  request_sent_to_client: "Send",
  keep_open:             "Clock",
};

const SOURCE_BADGES = {
  drl_needed_to_resolve: { label: "Allison proposed",               cls: "bg-violet-50 text-violet-700 border border-violet-200" },
  existing_advisory:     { label: "Advisory requirement",           cls: "bg-amber-50 text-amber-700 border border-amber-200"    },
  canonical_requirement: { label: "Canonical requirement",          cls: "bg-blue-50 text-blue-700 border border-blue-200"       },
  doc_request_link:      { label: "Document request",               cls: "bg-slate-50 text-slate-600 border border-slate-200"   },
  // Stage 2: client declared this document unavailable — requires governed staff decision
  unavailable_document:  { label: "Client declared unavailable",    cls: "bg-rose-50 text-rose-700 border border-rose-200"      },
};

const GOVERNANCE_BADGE = {
  governing: { label: "Production blocking", cls: "bg-rose-50 text-rose-700 border border-rose-200" },
  proposed:  { label: "Proposed",            cls: "bg-violet-50 text-violet-700 border border-violet-200" },
  warning:   { label: "Advisory",            cls: "bg-amber-50 text-amber-700 border border-amber-200"   },
};

// ─── helpers ─────────────────────────────────────────────────────────────────

function isTerminal(item) {
  return TERMINAL_DISPOSITIONS.has(item?.disposition);
}

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return iso;
  }
}

// ─── sub-components ──────────────────────────────────────────────────────────

function Badge({ children, cls = "" }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border ${cls}`}>
      {children}
    </span>
  );
}

function ItemDisposedAudit({ item }) {
  const IconComp = Icons[DISPOSITION_ICON[item.disposition]] || Icons.CheckCircle;
  return (
    <div
      className="mt-2 flex items-start gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] text-emerald-800"
      data-testid={`item-audit-${item.item_id}`}
    >
      <IconComp className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <span className="font-semibold">{DISPOSITION_LABELS[item.disposition] || item.disposition}</span>
        {item.disposed_by_name && <span className="text-emerald-700 ml-1">by {item.disposed_by_name}</span>}
        {item.disposed_at && <span className="text-emerald-600 ml-1">· {fmtDate(item.disposed_at)}</span>}
        {item.disposition_note && (
          <p className="mt-0.5 text-emerald-700 italic">"{item.disposition_note}"</p>
        )}
        {item.linked_request_id && (
          <p className="mt-0.5 text-emerald-600">Request ID: {item.linked_request_id}</p>
        )}
        {item.linked_doc_id && (
          <p className="mt-0.5 text-emerald-600">Document ID: {item.linked_doc_id}</p>
        )}
      </div>
    </div>
  );
}

function RequestSentBadge({ item }) {
  return (
    <div
      className="mt-2 flex items-start gap-2 rounded-xl bg-blue-50 border border-blue-200 px-3 py-2 text-[11px] text-blue-800"
      data-testid={`item-request-sent-${item.item_id}`}
    >
      <Icons.Send className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
      <div>
        <span className="font-semibold">Requested from client</span>
        {item.disposed_by_name && <span className="ml-1 text-blue-700">by {item.disposed_by_name}</span>}
        {item.disposed_at && <span className="ml-1 text-blue-600">· {fmtDate(item.disposed_at)}</span>}
        <p className="mt-0.5 text-blue-600">Request ID: {item.linked_request_id || "—"}</p>
        <p className="mt-0.5 text-blue-500">Awaiting client upload — blocker remains active.</p>
      </div>
    </div>
  );
}

/** Stage 2: display the original client unavailability declaration for staff review. */
function ClientDeclarationBanner({ item }) {
  if (item.source !== "unavailable_document") return null;
  // The description field contains the explanation prefixed by "Client declared..."
  // We also try to surface the raw explanation from the description text.
  const explanation = item.description || "";
  return (
    <div
      className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 space-y-1"
      data-testid={`client-declaration-banner-${item.item_id}`}
    >
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-rose-700 uppercase tracking-wide">
        <Icons.AlertTriangle className="h-3.5 w-3.5" />
        Client Declaration — Document Unavailable
      </div>
      <p className="text-xs text-rose-800 leading-relaxed" data-testid={`client-declaration-text-${item.item_id}`}>
        {explanation}
      </p>
      <p className="text-[10px] text-rose-500 italic">
        A staff decision is required. This declaration does not automatically satisfy any
        production prerequisite.
      </p>
    </div>
  );
}

/** Single document blocker item with full action panel. */
function BlockerItem({
  item,
  isExpanded,
  onToggle,
  onDispose,
  onRequestFromClient,
  loading,
  token,
  apiBase,
}) {
  const [action, setAction]   = useState("");
  const [note, setNote]       = useState("");
  const [linkedDoc, setLinkedDoc] = useState("");
  const [confirming, setConfirming] = useState(false);

  const sourceBadge = SOURCE_BADGES[item.source] || SOURCE_BADGES.existing_advisory;
  const govBadge    = GOVERNANCE_BADGE[item.governance_state] || GOVERNANCE_BADGE.warning;
  const terminal    = isTerminal(item);
  const requestSent = item.disposition === "request_sent_to_client";

  function handleActionSelect(a) {
    setAction(a);
    setNote("");
    setLinkedDoc("");
    setConfirming(false);
  }

  async function handleSubmit() {
    if (!action) return;
    if (NOTES_REQUIRED_FOR.has(action) && !note.trim()) {
      toast.error(`A note is required for "${DISPOSITION_LABELS[action]}"`);
      return;
    }
    await onDispose(item.item_id, action, note.trim() || null, linkedDoc.trim() || null);
    setAction("");
    setNote("");
    setLinkedDoc("");
    setConfirming(false);
  }

  async function handleRequestFromClient() {
    setConfirming(false);
    await onRequestFromClient(item.item_id);
  }

  const IconComp = Icons.FileText;

  return (
    <div
      className={`rounded-2xl border transition-all duration-150 ${
        terminal
          ? "border-emerald-200 bg-emerald-50/30"
          : requestSent
          ? "border-blue-200 bg-blue-50/20"
          : "border-slate-200 bg-white"
      }`}
      data-testid={`blocker-item-${item.item_id}`}
    >
      {/* Header row */}
      <button
        className="w-full flex items-start gap-3 px-4 py-3 text-left"
        onClick={onToggle}
        data-testid={`blocker-item-toggle-${item.item_id}`}
      >
        <div className="mt-0.5">
          {terminal ? (
            <Icons.CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
          ) : requestSent ? (
            <Icons.Send className="h-4 w-4 text-blue-500 flex-shrink-0" />
          ) : (
            <Icons.Circle className="h-4 w-4 text-slate-400 flex-shrink-0" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold leading-tight ${terminal ? "text-emerald-700 line-through decoration-emerald-400" : "text-slate-800"}`}>
            {item.label}
          </p>
          <div className="mt-1 flex flex-wrap gap-1">
            <Badge cls={sourceBadge.cls}>{sourceBadge.label}</Badge>
            {item.governance_state !== "warning" && (
              <Badge cls={govBadge.cls}>{govBadge.label}</Badge>
            )}
            {item.blocking_level === "advisory" && item.governance_state !== "governing" && (
              <Badge cls="bg-slate-50 text-slate-500 border border-slate-200">Advisory</Badge>
            )}
          </div>
        </div>
        {isExpanded ? (
          <Icons.ChevronUp className="h-4 w-4 text-slate-400 flex-shrink-0 mt-1" />
        ) : (
          <Icons.ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0 mt-1" />
        )}
      </button>

      {/* Expanded body */}
      {isExpanded && (
        <div className="px-4 pb-4" data-testid={`blocker-item-body-${item.item_id}`}>
          {/* Stage 2: show original client declaration for unavailable_document items */}
          <ClientDeclarationBanner item={item} />

          {item.description && item.source !== "unavailable_document" && (
            <p className="text-xs text-slate-500 mb-3 leading-relaxed">{item.description}</p>
          )}
          {item.doc_type_code && (
            <p className="text-[10px] text-slate-400 mb-2 font-mono">Type: {item.doc_type_code}</p>
          )}

          {/* Audit section for disposed items */}
          {terminal && <ItemDisposedAudit item={item} />}
          {requestSent && !terminal && <RequestSentBadge item={item} />}

          {/* Action panel — only for open items */}
          {!terminal && (
            <div className="mt-3 space-y-3">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                Staff disposition
              </p>

              {/* Action selector */}
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  { id: "received",              label: "Document Received",             icon: "CheckCircle2" },
                  { id: "satisfied_by_existing", label: "Satisfied by Existing",         icon: "FolderCheck"  },
                  { id: "not_applicable",        label: "Not Required",                  icon: "XCircle"      },
                  { id: "proceed_without",       label: "Proceed Without",               icon: "UserCheck"    },
                  { id: "duplicate",             label: "Duplicate / Superseded",        icon: "Copy"         },
                  { id: "request_from_client",   label: "Request From Client",           icon: "Send"         },
                  { id: "keep_open",             label: "Keep Open",                     icon: "Clock"        },
                ].map(({ id, label, icon }) => {
                  const Ic = Icons[icon] || Icons.Circle;
                  const isSelected = action === id || (id === "request_from_client" && action === "request_from_client");
                  return (
                    <button
                      key={id}
                      className={`flex items-center gap-1.5 rounded-xl border px-2.5 py-1.5 text-[11px] font-medium transition-all ${
                        isSelected
                          ? "border-blue-500 bg-blue-50 text-blue-700 ring-1 ring-blue-400"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                      onClick={() => handleActionSelect(id)}
                      data-testid={`action-btn-${id}-${item.item_id}`}
                    >
                      <Ic className="h-3.5 w-3.5 flex-shrink-0" />
                      {label}
                    </button>
                  );
                })}
              </div>

              {/* Note input for selected action */}
              {action && action !== "request_from_client" && action !== "keep_open" && (
                <div className="space-y-2">
                  <textarea
                    className={`w-full rounded-xl border px-3 py-2 text-xs text-slate-700 placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                      NOTES_REQUIRED_FOR.has(action) ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"
                    }`}
                    rows={2}
                    placeholder={
                      NOTES_REQUIRED_FOR.has(action)
                        ? `Note required for "${DISPOSITION_LABELS[action]}" — explain the basis.`
                        : `Optional staff note for "${DISPOSITION_LABELS[action]}"`
                    }
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    data-testid={`note-input-${item.item_id}`}
                  />
                  {(action === "received" || action === "satisfied_by_existing" || action === "duplicate") && (
                    <input
                      type="text"
                      className={`w-full rounded-xl border px-3 py-1.5 text-xs text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                        action === "satisfied_by_existing" && item.source === "unavailable_document"
                          ? "border-amber-300 bg-amber-50"
                          : "border-slate-200 bg-slate-50"
                      }`}
                      placeholder={
                        action === "received"
                          ? "Link evidence / document request ID (optional)"
                          : action === "duplicate"
                          ? "Link the item/document this duplicates (optional)"
                          : item.source === "unavailable_document"
                          ? "Satisfying document ID — required (validated server-side)"
                          : "Link satisfying document ID (optional)"
                      }
                      value={linkedDoc}
                      onChange={(e) => setLinkedDoc(e.target.value)}
                      data-testid={`linked-doc-input-${item.item_id}`}
                    />
                  )}
                  <button
                    className="w-full rounded-xl bg-slate-800 text-white text-xs font-semibold py-2 hover:bg-slate-700 active:bg-slate-900 transition-colors disabled:opacity-50"
                    onClick={handleSubmit}
                    disabled={loading || (NOTES_REQUIRED_FOR.has(action) && !note.trim())}
                    data-testid={`submit-disposition-${item.item_id}`}
                  >
                    {loading ? "Saving…" : `Apply: ${DISPOSITION_LABELS[action] || action}`}
                  </button>
                </div>
              )}

              {/* Request from client action */}
              {action === "request_from_client" && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-3 space-y-2">
                  {item.source === "unavailable_document" ? (
                    <p className="text-[11px] text-blue-700 leading-relaxed" data-testid={`request-from-client-desc-${item.item_id}`}>
                      The original document request will be <strong>reactivated</strong> in the client portal — the total document count will <strong>not</strong> increase.
                      The client's original unavailability declaration is preserved in the request audit history.
                      Responsibility transfers to <strong>CLIENT</strong> until they supply alternative evidence or re-declare unavailable.
                    </p>
                  ) : (
                    <p className="text-[11px] text-blue-700 leading-relaxed" data-testid={`request-from-client-desc-${item.item_id}`}>
                      A canonical document request will be created and sent to the client.
                      Responsibility for this item transfers to <strong>CLIENT</strong>.
                      The document_missing blocker <strong>remains active</strong> until a terminal disposition is applied.
                      This action is idempotent — repeated requests link to the existing request.
                    </p>
                  )}
                  <button
                    className="w-full rounded-xl bg-blue-600 text-white text-xs font-semibold py-2 hover:bg-blue-500 active:bg-blue-700 transition-colors disabled:opacity-50"
                    onClick={handleRequestFromClient}
                    disabled={loading || requestSent}
                    data-testid={`request-from-client-${item.item_id}`}
                  >
                    {loading
                      ? "Sending request…"
                      : requestSent
                      ? "Request already sent"
                      : item.source === "unavailable_document"
                      ? "Reactivate — Request Alternative Information"
                      : "Send Document Request to Client"}
                  </button>
                </div>
              )}

              {/* Keep open - confirmation */}
              {action === "keep_open" && (
                <button
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 text-slate-600 text-xs font-semibold py-2 hover:bg-slate-100 transition-colors"
                  onClick={() => {
                    setAction("");
                    onToggle();
                  }}
                  data-testid={`keep-open-${item.item_id}`}
                >
                  Acknowledge — keep open (no change)
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── main panel component ─────────────────────────────────────────────────────

export function ResolveDocumentBlockerPanel({
  open,
  onClose,
  clientId,
  engagementId,
  drlData,
  runDrl,
  onRefreshCockpit,
  apiBase,
  token,
}) {
  const [items, setItems]         = useState([]);
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [finalResult, setFinalResult] = useState(null);
  const [error, setError]         = useState(null);
  const panelRef = useRef(null);

  // Dismiss all advisory items modal state
  const [showDismissModal, setShowDismissModal] = useState(false);
  const [dismissReason, setDismissReason]       = useState("");
  const [dismissing, setDismissing]             = useState(false);

  // Keyboard: close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  // ── data fetching ─────────────────────────────────────────────────────────

  const loadItems = useCallback(async (withRefresh = false) => {
    if (!clientId || !engagementId || !token) return null;
    setLoading(true);
    setError(null);
    try {
      let data;
      if (withRefresh) {
        // POST /refresh with DRL needed_to_resolve
        const body = {
          needed_to_resolve: drlData?.needed_to_resolve || [],
        };
        const res = await fetch(
          `${apiBase}/staff-assist/document-blocker/${clientId}/${engagementId}/refresh`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        );
        data = await safeJson(res);
        if (!res.ok) throw new Error(data?.detail || "Refresh failed");
      } else {
        const res = await fetch(
          `${apiBase}/staff-assist/document-blocker/${clientId}/${engagementId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        data = await safeJson(res);
        if (!res.ok) throw new Error(data?.detail || "Failed to load blocker items");
      }
      setItems(data.items || []);
      setSummary(data);
      return data;
    } catch (e) {
      setError(e.message || "Failed to load document blocker items.");
      return null;
    } finally {
      setLoading(false);
    }
  }, [clientId, engagementId, token, apiBase, drlData]);

  // Load on open; also trigger a refresh to pull DRL proposals on first load
  useEffect(() => {
    if (open) {
      setFinalResult(null);
      setExpandedIds(new Set());
      loadItems(true); // refresh to materialize DRL proposals
    }
  }, [open, loadItems]);

  // ── mutations ─────────────────────────────────────────────────────────────

  async function handleDispose(itemId, action, note, linkedDocId) {
    setActionLoading(true);
    try {
      const res = await fetch(
        `${apiBase}/staff-assist/document-blocker/${clientId}/${engagementId}/items/${itemId}/disposition`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            action,
            note:           note || null,
            linked_doc_id:  linkedDocId || null,
          }),
        }
      );
      const data = await safeJson(res);
      if (!res.ok) {
        const msg = data?.detail?.detail || data?.detail || "Disposition failed";
        toast.error(msg);
        return;
      }

      toast.success(DISPOSITION_LABELS[action] || action);

      // Reload items and check suppression from the same fetch
      const freshSummary = await loadItems(false);

      // Refresh DRL presentation, Cockpit, and Journey
      runDrl?.();
      onRefreshCockpit?.();

      if (freshSummary?.suppressed) {
        setFinalResult({
          type: "suppressed",
          message:
            "All governing document requirements have been resolved. " +
            "The document_missing blocker has been cleared. " +
            "Journey and LiveOps will reflect the next canonical obligation.",
        });
      }
    } catch (e) {
      toast.error(e.message || "Disposition request failed");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleRequestFromClient(itemId) {
    setActionLoading(true);
    try {
      const res = await fetch(
        `${apiBase}/staff-assist/document-blocker/${clientId}/${engagementId}/items/${itemId}/request-from-client`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({}),
        }
      );
      const data = await safeJson(res);
      if (!res.ok) {
        const msg = data?.detail?.detail || data?.detail || "Request failed";
        toast.error(msg);
        return;
      }

      if (data.already_sent) {
        toast.info("Document request already sent — linked to existing request.");
      } else {
        toast.success("Document request sent to client. Responsibility → CLIENT.");
      }

      // Reload items
      await loadItems(false);

      // Refresh DRL and Cockpit
      runDrl?.();
      onRefreshCockpit?.();
    } catch (e) {
      toast.error(e.message || "Request failed");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDismissAllAdvisory() {
    const trimmedReason = dismissReason.trim();
    if (trimmedReason.length < 4) {
      toast.error("Please provide a reason of at least 4 characters.");
      return;
    }
    setDismissing(true);
    try {
      const res = await fetch(
        `${apiBase}/staff-assist/document-blocker/${clientId}/${engagementId}/dismiss-advisory`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ reason: trimmedReason }),
        }
      );
      const data = await safeJson(res);
      if (!res.ok) {
        const msg = data?.detail?.detail || data?.detail || "Dismissal failed";
        toast.error(msg);
        return;
      }
      const count = data.dismissed ?? 0;
      if (count > 0) {
        toast.success(`${count} advisory item${count !== 1 ? "s" : ""} deferred. Reason recorded.`);
      } else {
        toast.info("No open advisory items to dismiss.");
      }
      setShowDismissModal(false);
      setDismissReason("");
      await loadItems(false);
      runDrl?.();
      onRefreshCockpit?.();
    } catch (e) {
      toast.error(e.message || "Dismissal request failed");
    } finally {
      setDismissing(false);
    }
  }

  function toggleExpand(id) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // ── grouping ──────────────────────────────────────────────────────────────

  const productionItems = items.filter(
    (i) => i.governance_state === "governing" && i.blocking_level === "production"
  );
  const advisoryItems = items.filter(
    (i) => i.governance_state !== "governing" || i.blocking_level !== "production"
  );

  const openProduction  = productionItems.filter((i) => !isTerminal(i));
  const closedProduction = productionItems.filter((i) => isTerminal(i));

  const openAdvisory = advisoryItems.filter((i) => !isTerminal(i));

  const resolvedCount = summary?.resolved_governing_production || 0;
  const totalCount    = summary?.total_governing_production    || 0;
  const suppressed    = summary?.suppressed || false;
  const noAdvisoryData = summary?.no_advisory_data || false;
  const progress      = totalCount > 0 ? Math.round((resolvedCount / totalCount) * 100) : 0;

  // ── render ────────────────────────────────────────────────────────────────

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/20 backdrop-blur-[1px] z-40 transition-opacity duration-200"
        onClick={onClose}
        data-testid="document-blocker-panel-backdrop"
        aria-hidden="true"
      />

      {/* Slide-over panel */}
      <div
        ref={panelRef}
        className="fixed inset-y-0 right-0 z-50 flex flex-col w-full max-w-2xl bg-white shadow-2xl shadow-slate-900/20 overflow-hidden"
        data-testid="document-blocker-panel"
      >
        {/* ── Panel header ──────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-white flex-shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <Icons.ShieldAlert className="h-5 w-5 text-rose-600" />
              <h2 className="text-base font-bold text-slate-900" data-testid="document-blocker-panel-title">
                Resolve Document Blocker
              </h2>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Governed resolution console — engagement-scoped document requirements
            </p>
          </div>
          <div className="flex items-center gap-2">
            {engagementId && (
              <span className="text-[10px] font-mono text-slate-400 bg-slate-50 px-2 py-1 rounded-lg border border-slate-200">
                {engagementId.slice(0, 8)}…
              </span>
            )}
            <button
              className="rounded-xl p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              onClick={onClose}
              data-testid="document-blocker-panel-close"
              aria-label="Close document blocker panel"
            >
              <Icons.X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* ── Progress bar ──────────────────────────────────────────────── */}
        {!suppressed && totalCount > 0 && (
          <div className="px-6 py-3 border-b border-slate-100 bg-slate-50 flex-shrink-0" data-testid="document-blocker-progress">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-slate-600">
                Production blockers resolved
              </span>
              <span className="text-xs font-bold text-slate-800">
                {resolvedCount}/{totalCount}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
              <div
                className="h-full rounded-full bg-rose-500 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* ── Final resolution banner ──────────────────────────────────── */}
        {(suppressed || finalResult?.type === "suppressed") && (
          <div
            className="mx-4 mt-4 flex items-start gap-3 rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-4 flex-shrink-0"
            data-testid="document-blocker-suppressed-banner"
          >
            <Icons.CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-emerald-800">Document requirements cleared</p>
              <p className="text-xs text-emerald-700 mt-0.5 leading-relaxed">
                {finalResult?.message ||
                  "All governed document requirements have been resolved. " +
                  "The document_missing blocker has been cleared. " +
                  "The Cockpit will surface the next canonical obligation."}
              </p>
            </div>
          </div>
        )}

        {/* ── Scrollable content ────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto" data-testid="document-blocker-panel-body">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Icons.Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              <span className="ml-2 text-sm text-slate-500">Loading document requirements…</span>
            </div>
          )}

          {error && (
            <div className="mx-4 mt-4 flex items-start gap-2 rounded-2xl bg-rose-50 border border-rose-200 px-4 py-3">
              <Icons.AlertCircle className="h-4 w-4 text-rose-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-rose-700">{error}</p>
            </div>
          )}

          {!loading && !error && (
            <div className="px-4 py-4 space-y-6">

              {/* ── Section 1: Production Blocking Items ──────────────── */}
              <section data-testid="section-production-blocking">
                <div className="flex items-center gap-2 mb-3">
                  <Icons.AlertTriangle className="h-4 w-4 text-rose-600" />
                  <h3 className="text-sm font-bold text-slate-800">
                    Production-Blocking Requirements
                  </h3>
                  <Badge cls="bg-rose-50 text-rose-700 border border-rose-200">
                    {openProduction.length} open
                  </Badge>
                </div>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                  All items below must be resolved before the document_missing blocker is cleared.
                  Notes are required for judgment-based dispositions.
                </p>

                {productionItems.length === 0 && (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-8 text-center">
                    <Icons.FileQuestion className="h-6 w-6 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">No production-blocking requirements found.</p>
                    <p className="text-xs text-slate-400 mt-1">Use Refresh to detect requirements from the latest advisory assessment.</p>
                  </div>
                )}

                <div className="space-y-2">
                  {/* Open items first */}
                  {openProduction.map((item) => (
                    <BlockerItem
                      key={item.item_id}
                      item={item}
                      isExpanded={expandedIds.has(item.item_id)}
                      onToggle={() => toggleExpand(item.item_id)}
                      onDispose={handleDispose}
                      onRequestFromClient={handleRequestFromClient}
                      loading={actionLoading}
                      token={token}
                      apiBase={apiBase}
                    />
                  ))}

                  {/* Resolved items (collapsed by default) */}
                  {closedProduction.length > 0 && (
                    <div data-testid="section-resolved-production">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide my-2 px-1">
                        Resolved ({closedProduction.length})
                      </p>
                      {closedProduction.map((item) => (
                        <BlockerItem
                          key={item.item_id}
                          item={item}
                          isExpanded={expandedIds.has(item.item_id)}
                          onToggle={() => toggleExpand(item.item_id)}
                          onDispose={handleDispose}
                          onRequestFromClient={handleRequestFromClient}
                          loading={actionLoading}
                          token={token}
                          apiBase={apiBase}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </section>

              {/* ── Section 2: Warnings / Advisory / Proposed ─────────── */}
              {advisoryItems.length > 0 && (
                <section data-testid="section-advisory-items">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Icons.Info className="h-4 w-4 text-amber-600" />
                      <h3 className="text-sm font-bold text-slate-700">
                        Warnings &amp; Allison Proposals
                      </h3>
                      <Badge cls="bg-amber-50 text-amber-700 border border-amber-200">
                        {advisoryItems.length}
                      </Badge>
                    </div>
                    {/* Batch dismiss — only shown when open advisory items exist */}
                    {openAdvisory.length > 0 && (
                      <button
                        className="flex items-center gap-1.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-50"
                        onClick={() => { setShowDismissModal(true); setDismissReason(""); }}
                        disabled={loading || actionLoading || dismissing}
                        data-testid="dismiss-all-advisory-btn"
                        title="Defer all open advisory items with a reason — does not affect production-blocking requirements"
                      >
                        <Icons.CheckCheck className="h-3.5 w-3.5" />
                        Dismiss All Advisory ({openAdvisory.length})
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                    Advisory items and Allison-proposed requirements.
                    These cannot trigger suppression — they are informational.
                    Production-blocking disposition still available where appropriate.
                  </p>
                  <div className="space-y-2">
                    {advisoryItems.map((item) => (
                      <BlockerItem
                        key={item.item_id}
                        item={item}
                        isExpanded={expandedIds.has(item.item_id)}
                        onToggle={() => toggleExpand(item.item_id)}
                        onDispose={handleDispose}
                        onRequestFromClient={handleRequestFromClient}
                        loading={actionLoading}
                        token={token}
                        apiBase={apiBase}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Empty state */}
              {!loading && items.length === 0 && !error && (
                <div className="flex flex-col items-center justify-center py-10 text-center px-4" data-testid="document-blocker-empty-state">
                  {noAdvisoryData ? (
                    <>
                      <Icons.FolderSearch className="h-8 w-8 text-amber-300 mb-3" />
                      <p className="text-sm font-semibold text-slate-600" data-testid="document-blocker-no-advisory-label">
                        No advisory assessment for this engagement
                      </p>
                      <p className="text-xs text-slate-500 mt-2 max-w-xs leading-relaxed">
                        Document requirements have not been detected for this engagement yet.
                        Run <span className="font-semibold text-slate-600">Prepare Document Advisory</span> from the Allison panel to detect and classify any outstanding document requirements.
                      </p>
                      <p className="text-xs text-slate-400 mt-3">
                        If requirements were detected and all items are already resolved, the panel will show the cleared banner.
                      </p>
                    </>
                  ) : (
                    <>
                      <Icons.FolderOpen className="h-8 w-8 text-slate-200 mb-3" />
                      <p className="text-sm font-semibold text-slate-400">No document requirements found</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Use Refresh to detect requirements from the latest advisory assessment.
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Panel footer ──────────────────────────────────────────────── */}
        <div
          className="flex items-center justify-between px-6 py-3 border-t border-slate-100 bg-slate-50 flex-shrink-0"
          data-testid="document-blocker-panel-footer"
        >
          <div className="flex items-center gap-2">
            <button
              className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-50"
              onClick={() => loadItems(true)}
              disabled={loading || actionLoading}
              data-testid="document-blocker-refresh-btn"
            >
              <Icons.RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh Requirements
            </button>
          </div>
          <div className="flex items-center gap-2">
            {/* "Go to Documents" remains as secondary action inside panel */}
            {clientId && (
              <a
                href={`/workspace/${clientId}?tab=documents`}
                target="_self"
                className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                data-testid="document-blocker-go-to-documents"
              >
                <Icons.FolderOpen className="h-3.5 w-3.5" />
                Go to Documents
              </a>
            )}
            <button
              className="rounded-xl bg-slate-800 text-white px-4 py-1.5 text-xs font-semibold hover:bg-slate-700 transition-colors"
              onClick={onClose}
              data-testid="document-blocker-done-btn"
            >
              Done
            </button>
          </div>
        </div>
      </div>

      {/* ── Dismiss Advisory Modal ────────────────────────────────────── */}
      {showDismissModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          data-testid="dismiss-advisory-modal-overlay"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            onClick={() => !dismissing && setShowDismissModal(false)}
          />
          {/* Modal */}
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 z-10"
            data-testid="dismiss-advisory-modal"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 rounded-xl bg-amber-50 border border-amber-200 flex-shrink-0">
                <Icons.CheckCheck className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  Dismiss All Advisory Items
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Defers {openAdvisory.length} open advisory item{openAdvisory.length !== 1 ? "s" : ""} with
                  a staff reason. Production-blocking requirements are not affected.
                </p>
              </div>
            </div>

            {/* Safety notice */}
            <div className="rounded-xl bg-blue-50 border border-blue-200 px-3 py-2.5 mb-4 text-xs text-blue-700 space-y-1">
              <p className="font-semibold flex items-center gap-1.5">
                <Icons.ShieldCheck className="h-3.5 w-3.5" /> Safeguards active
              </p>
              <ul className="list-disc list-inside space-y-0.5 text-blue-600 pl-1">
                <li>Production-blocking items are never affected</li>
                <li>Advisory records remain in case history</li>
                <li>No documents are published or communications sent</li>
                <li>Dismissed items can be viewed individually</li>
              </ul>
            </div>

            {/* Reason input */}
            <label className="block mb-4">
              <span className="text-xs font-semibold text-slate-700 mb-1.5 block">
                Reason for deferring advisory items <span className="text-rose-500">*</span>
              </span>
              <textarea
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 resize-none focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-300 transition-all"
                rows={3}
                placeholder="e.g. Engagement complete — STR delivered, advisory items noted for record…"
                value={dismissReason}
                onChange={(e) => setDismissReason(e.target.value)}
                disabled={dismissing}
                data-testid="dismiss-advisory-reason-input"
                maxLength={500}
              />
              <span className="text-[10px] text-slate-400 float-right mt-0.5">
                {dismissReason.length}/500
              </span>
            </label>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
                onClick={() => setShowDismissModal(false)}
                disabled={dismissing}
                data-testid="dismiss-advisory-cancel-btn"
              >
                Cancel
              </button>
              <button
                className="flex items-center gap-1.5 rounded-xl bg-amber-600 text-white px-4 py-2 text-xs font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50"
                onClick={handleDismissAllAdvisory}
                disabled={dismissing || dismissReason.trim().length < 4}
                data-testid="dismiss-advisory-confirm-btn"
              >
                {dismissing ? (
                  <><Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> Dismissing…</>
                ) : (
                  <><Icons.CheckCheck className="h-3.5 w-3.5" /> Confirm Dismissal</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}